import * as React from "react";
import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useAsync } from "react-use";
import { Timespan, TimespanPager } from "./TimespanPager";
import { VizDTO, IVizDTO, useGroopitApiContext } from "groopit-js";
import { getGroupingTypes } from "../VizTypeChooser";
import FormatDecimal from "../FormatDecimal";
import {
  ComponentOperationType,
  GroopitFilterAssignmentDTO,
  GroopitFilterComponentDTO,
  GroopitFilterDTO,
} from "groopit-js/client";

interface TagCount {
  name: string;
  total: number;
  url?: string;
  format: string;
}

const Tag = (props: TagCount) => {
  const { name, total, url, format } = props;
  return (
    <tr>
      <td className="value text-4xl text-app-orange pl-8 pr-4 text-right">
        {FormatDecimal(total, format)}
      </td>
      <td>
        <div className="divider"></div>
      </td>
      <td className="w-full text-teal text-break-words">
        {url ? (
          // This always links to us
          // eslint-disable-next-line react/jsx-no-target-blank
          <a
            href={url}
            target={window.self === window.top ? "_self" : "_blank"}
            className="link-groopit"
          >
            {name}
          </a>
        ) : (
          <div className="link-groopit">{name}</div>
        )}
      </td>
    </tr>
  );
};

const page_size = 10;

interface TagCountProps extends Omit<IVizDTO, "title" | "descripton"> {
  supportsFeedDateFilters: boolean;
}

const TagCountViz = (props: TagCountProps) => {
  const { viz } = useGroopitApiContext();
  const [page, setPage] = useState(1);
  const { loading, value: data } = useAsync(async () => {
    const json = await viz.getDataPOST(new VizDTO({ title: "", ...props }));
    return json as TagCount[];
  }, [props]);

  if (loading || !data) {
    return <div>Loading...</div>;
  }
  if (data.length == 0) {
    return <div>no data...</div>;
  }
  const partial = data.length > page * page_size;

  //
  // Calculate the right URL to take the user to a filtered view of
  // groopits. This includes the tag and the date range (if any
  // and the client supports it).
  //
  const getFeedUrl = (tag: TagCount) => {
    //
    // If end date filtering is enabled and we're on a client that doesn't support
    // date filtering in the feed, don't generate a URL.
    //
    if (
      !props.supportsFeedDateFilters &&
      props.endDate &&
      props.endDate < new Date()
    ) {
      return undefined;
    }

    const filter = new GroopitFilterDTO({
      assignments: [
        new GroopitFilterAssignmentDTO({
          id: props.assignmentId,
          startDate: props.startDate,
          endDate: props.endDate,
          components: [
            new GroopitFilterComponentDTO({
              componentModelId: props.filterId ?? props.primaryId,
              operation: ComponentOperationType.AnyOf,
              value: [tag.name],
            }),
          ],
        }),
      ],
    });

    return `/Groop/Groopits/${props.groopId}?filter=${encodeURIComponent(
      JSON.stringify(filter),
    )}`;
  };

  return (
    <div className="leaderboard-viz">
      {page > 1 ? (
        <div>
          Top {(page - 1) * page_size + 1} -{" "}
          {Math.min(data.length, page * page_size)}
        </div>
      ) : (
        <div>Top {Math.min(data.length, page_size)}</div>
      )}
      <table>
        <tbody>
          {data
            .slice((page - 1) * page_size, page * page_size)
            .map((tag, i) => (
              <Tag
                key={i}
                name={tag.name}
                total={tag.total}
                format={tag.format}
                url={getFeedUrl(tag)}
              ></Tag>
            ))}
        </tbody>
      </table>
      <div className="text-color-primary text-display-subtitle">
        {page > 1 && (
          <button className="pull-left" onClick={() => setPage(page - 1)}>
            <span
              className="glyphicon glyphicon-chevron-left"
              aria-hidden="true"
            ></span>
            PREV
          </button>
        )}
        {partial && (
          <button className="pull-right" onClick={() => setPage(page + 1)}>
            NEXT
            <span
              className="glyphicon glyphicon-chevron-right"
              aria-hidden="true"
            ></span>
          </button>
        )}
      </div>
    </div>
  );
};

export const TagCountVizWrapper = (props: TagCountProps): JSX.Element => {
  const [timespan, setTimespan] = React.useState(
    new Timespan(new Date(), props.grouping, props.startDate, props.endDate),
  );
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: "100px 0px",
  });
  useEffect(() => {
    if (
      props.startDate != timespan.startDate ||
      props.endDate != timespan.endDate ||
      props.grouping != timespan.grouping
    ) {
      setTimespan(
        new Timespan(
          timespan.date,
          props.grouping,
          props.startDate,
          props.endDate,
        ),
      );
    }
  }, [props.startDate, props.endDate, props.grouping]);

  return (
    <div ref={ref}>
      <TimespanPager
        timespan={timespan}
        on_change={setTimespan}
        groupings={getGroupingTypes(props.vizType, props.operation)}
      />
      {inView && (
        <TagCountViz
          {...props}
          grouping={timespan.grouping}
          startDate={timespan.start}
          endDate={timespan.end}
        />
      )}
    </div>
  );
};
